import { IpRestrictionForm } from './IpRestrictionForm';
import { GroupConsoleTitle } from '@user/pages/GroupConsole/common';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { RTDBPath, defaultIpRestrictionConfiguration } from 'balus-common';
import type { GroupEntity } from '@group/domain';
import type { IpRestrictionConfiguration } from 'balus-common';
import { Navigate } from 'react-router';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { useCurrentSessionId } from '@framework/auth/authStatus';
import { useGroupConsoleOutletContext } from '../WithGroupConsoleMenu';
import { useGroupLicensePermissions } from '@group/License';
import { useTranslation } from 'react-i18next';
import { trpcClient } from '@framework/trpcClient';

export const GroupConsoleIpRestrictionPage: React.FC = () => {
  const { group } = useGroupConsoleOutletContext();
  const [isEnabledIpRestrictionFeature, { loading }] = useSnapshot<boolean>({
    path: RTDBPath.Group.ipRestrictionEnabledPath(group.id),
    load({ snapshot }) {
      return !!snapshot.val();
    },
  });

  const permissions = useGroupLicensePermissions();

  if (loading) {
    return null;
  }

  return isEnabledIpRestrictionFeature && permissions?.ipRestrictionCheck ? (
    <GroupConsoleIpRestrictionPageContent group={group} />
  ) : (
    <Navigate to={UserPagePathBuilder.groupConsolePage(group.id)} replace />
  );
};

type ContentProps = {
  group: GroupEntity;
};

const GroupConsoleIpRestrictionPageContent = ({ group }: ContentProps) => {
  const { t } = useTranslation();
  const [ipRestrictionConfiguration] = useSnapshot<IpRestrictionConfiguration>({
    path: RTDBPath.Group.ipRestrictionConfigurationPath(group.id),
    load({ snapshot }) {
      return (snapshot.val() as null | IpRestrictionConfiguration) ?? defaultIpRestrictionConfiguration;
    },
  });

  const saveIpRestrictionConfiguration = async (config: IpRestrictionConfiguration) => {
    await trpcClient.group.updateIpRestrictionConfiguration.mutate({
      groupId: group.id,
      configuration: config,
    });
  };

  const sessionId = useCurrentSessionId();
  const [currentIp] = useSnapshot({
    path: RTDBPath.User.activeAuthSessionCurrentIpPath(sessionId || ''),
    skip: !sessionId,
    load({ snapshot }) {
      return snapshot.val() as string | null;
    },
  });

  return (
    <div className="pb-8">
      <div className={'mb-4'}>
        <GroupConsoleTitle title={t('group.console.nav.ipRestriction')} />
      </div>
      <div className="mb-4 px-2">
        {ipRestrictionConfiguration && (
          <IpRestrictionForm
            ipRestrictionConfiguration={ipRestrictionConfiguration}
            saveIpRestrictionConfiguration={saveIpRestrictionConfiguration}
            currentIp={currentIp}
          />
        )}
      </div>
    </div>
  );
};
